import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  ListDivider,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Select,
  Option,
} from '@mui/joy';
import {
  Bank as AccountBalance,
  ArrowsClockwise as Refresh,
  Trash as Delete,
  Warning,
  CheckCircle,
  XCircle as ErrorIcon,
  ArrowsClockwise as Update,
  LinkBreak as LinkOff,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { BankConnection, BankAccount, ChartOfAccount } from '../../../types/accounting';
import PlaidLinkButton from './PlaidLinkButton';

export const BankConnections: React.FC = () => {
  const [connections, setConnections] = useState<BankConnection[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [chartAccounts, setChartAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<BankConnection | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    color: 'success' | 'danger' | 'warning';
  }>({ open: false, message: '', color: 'success' });

  const showSnackbar = (message: string, color: 'success' | 'danger' | 'warning') => {
    setSnackbar({ open: true, message, color });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [connectionsRes, accountsRes, coaRes] = await Promise.all([
        apiAccountingClient.getBankConnections(),
        apiAccountingClient.getBankAccounts(),
        apiAccountingClient.getChartOfAccounts(),
      ]);

      setConnections(connectionsRes.results);
      setAccounts(accountsRes.results);
      setChartAccounts(coaRes.results || []);
    } catch (err) {
      console.error('Failed to fetch bank connections:', err);
      setError('Failed to load bank connections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (connection: BankConnection) => {
    try {
      setSyncing(connection.id);
      const result = await apiAccountingClient.syncBankConnection(connection.id);

      if (result.success) {
        await fetchData();
        showSnackbar(`Successfully synced ${result.transactions_synced} transactions`, 'success');
      }
    } catch (err) {
      console.error('Failed to sync bank connection:', err);
      showSnackbar('Failed to sync bank connection. Please try again.', 'danger');
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async () => {
    if (!connectionToDelete) return;

    try {
      await apiAccountingClient.deleteBankConnection(connectionToDelete.id);
      setDeleteModalOpen(false);
      setConnectionToDelete(null);
      await fetchData();
      showSnackbar('Bank connection removed successfully.', 'success');
    } catch (err) {
      console.error('Failed to delete bank connection:', err);
      showSnackbar('Failed to delete bank connection. Please try again.', 'danger');
    }
  };

  const handleConnectionSuccess = async () => {
    await fetchData();
  };

  const handleAccountCoaMapping = async (accountId: number, chartAccountId: number | null) => {
    try {
      await apiAccountingClient.updateBankAccount(accountId, { chart_account: chartAccountId } as Partial<BankAccount>);
      showSnackbar('Account mapping updated.', 'success');
      // Update local state
      setAccounts(prev =>
        prev.map(acc =>
          acc.id === accountId ? { ...acc, chart_account: chartAccountId ?? undefined } : acc
        )
      );
    } catch (err) {
      console.error('Failed to update account mapping:', err);
      showSnackbar('Failed to update account mapping.', 'danger');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle color="var(--joy-palette-success-500)" />;
      case 'ERROR':
        return <ErrorIcon color="var(--joy-palette-danger-500)" />;
      case 'UPDATING':
        return <Update color="var(--joy-palette-warning-500)" />;
      case 'DISCONNECTED':
        return <LinkOff />;
      default:
        return <Warning color="var(--joy-palette-warning-500)" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'danger' | 'warning' | 'neutral' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'ERROR':
        return 'danger';
      case 'UPDATING':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter CoA to bank-type accounts for mapping dropdown
  const bankCoaAccounts = chartAccounts.filter(
    acc => acc.account_subtype === 'BANK' || acc.account_subtype === 'CREDIT_CARD'
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
        <div>
          <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
            Bank Connections
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Manage your connected bank accounts and sync transactions
          </Typography>
        </div>
        <PlaidLinkButton onSuccess={handleConnectionSuccess} />
      </Box>

      {connections.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AccountBalance size={48} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 16 }} />
            <Typography level="h4" sx={{ mb: 1 }}>
              No bank accounts connected
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3 }}>
              Connect your bank accounts to automatically import and categorize transactions
            </Typography>
            <PlaidLinkButton onSuccess={handleConnectionSuccess} size="lg" />
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {connections.map((connection) => {
            const connectionAccounts = accounts.filter(acc => acc.connection === connection.id);

            return (
              <Grid key={connection.id} xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccountBalance />
                        <div>
                          <Typography level="h4">{connection.institution_name}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            Connected {formatDate(connection.created_at)}
                          </Typography>
                        </div>
                      </Box>
                      <Chip
                        size="sm"
                        color={getStatusColor(connection.status)}
                        startDecorator={getStatusIcon(connection.status)}
                      >
                        {connection.status}
                      </Chip>
                    </Box>

                    {connection.error_message && (
                      <Alert color="danger" sx={{ mb: 2 }}>
                        {connection.error_message}
                      </Alert>
                    )}

                    <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                      Last synced: {formatDate(connection.last_synced)}
                    </Typography>

                    <List size="sm">
                      {connectionAccounts.map((account, index) => (
                        <React.Fragment key={account.id}>
                          {index > 0 && <ListDivider />}
                          <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ListItemDecorator>
                                <AccountBalance size={18} />
                              </ListItemDecorator>
                              <ListItemContent>
                                <Typography level="body-sm">{account.account_name}</Typography>
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                  {account.account_type} {account.account_number_mask && `(...${account.account_number_mask})`}
                                </Typography>
                              </ListItemContent>
                              <Typography level="body-md" fontWeight="lg">
                                {formatCurrency(account.current_balance)}
                              </Typography>
                            </Box>
                            {bankCoaAccounts.length > 0 && (
                              <Box sx={{ pl: 4.5 }}>
                                <Select
                                  size="sm"
                                  placeholder="Map to Chart of Account..."
                                  value={account.chart_account ? String(account.chart_account) : null}
                                  onChange={(_e, val) => handleAccountCoaMapping(account.id, val ? Number(val) : null)}
                                  sx={{ maxWidth: 280 }}
                                >
                                  {bankCoaAccounts.map(coa => (
                                    <Option key={coa.id} value={String(coa.id)}>
                                      {coa.account_number} - {coa.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Box>
                            )}
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>

                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<Refresh />}
                        onClick={() => handleSync(connection)}
                        loading={syncing === connection.id}
                        disabled={syncing !== null}
                      >
                        Sync Transactions
                      </Button>
                      {connection.status === 'ERROR' && (
                        <PlaidLinkButton onSuccess={handleConnectionSuccess} size="sm" />
                      )}
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="danger"
                        onClick={() => {
                          setConnectionToDelete(connection);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <Warning />
            Delete Bank Connection
          </DialogTitle>
          <DialogContent>
            Are you sure you want to delete the connection to {connectionToDelete?.institution_name}?
            This will remove all associated accounts and transactions.
          </DialogContent>
          <DialogActions>
            <Button variant="solid" color="danger" onClick={handleDelete}>
              Delete Connection
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        color={snackbar.color}
        variant="soft"
        startDecorator={snackbar.color === 'success' ? <CheckCircle /> : <Warning />}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};
